// Conversation file attachments: fetch, preview and save — always through the
// membership-gated API, resolved fresh from the message id on every open.
//
// Why this exists (2026-10-22, "the PDF opened earlier, now it errors"): the
// card used to point a bare <a href>/<img src> at the file endpoint. Two things
// broke that silently:
//   1. the bytes themselves vanished on the server at each deploy (fixed in
//      conversation-service: durable storage + a 410 "no longer available");
//   2. a bare link can't renew an expired 15-minute session cookie and can't
//      show a sensible message — the person got a raw JSON error tab or a
//      broken image.
// Here every open is an authenticated request that renews the session ONCE on
// a 401, retries ONCE on a network failure, and maps each failure to words.
// Nothing is cached: no URL, signed or otherwise, outlives the click.
import i18n from '@/i18n/config';
import { getApiErrorMessage } from '@/lib/apiError';

export type PreviewKind = 'pdf' | 'image' | 'none';

/** What the browser can show in place. Everything else (Office docs, archives…) downloads. */
export function previewKind(mime?: string, name?: string): PreviewKind {
  const m = (mime || '').toLowerCase();
  const ext = (name || '').toLowerCase().split('.').pop() || '';
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
  // SVG is excluded on purpose: it can carry script, and it's rare in chat.
  if ((m.startsWith('image/') && m !== 'image/svg+xml') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'none';
}

export type AttachmentFailure = 'gone' | 'forbidden' | 'not_found' | 'network' | 'server';

interface HttpLikeError {
  response?: { status?: number; data?: unknown };
  code?: string;
}

/** Classify a failed fetch. No response at all = the network, never "gone". */
export function classifyAttachmentError(e: unknown): AttachmentFailure {
  const status = (e as HttpLikeError)?.response?.status;
  if (!status) return 'network';
  if (status === 410) return 'gone';
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  return 'server';
}

/** Human message for a failed open. The server's own words win when it gave any. */
export function attachmentErrorMessage(e: unknown): string {
  const t = (k: string, d: string) => i18n.t(`conversations:file.${k}`, { defaultValue: d }) as string;
  switch (classifyAttachmentError(e)) {
    case 'gone':
      return getApiErrorMessage(e, t('gone', 'This file is no longer available. Ask the sender to share it again.'));
    case 'forbidden':
      return t('forbidden', 'Your session has ended or you no longer have access to this file. Sign in again and retry.');
    case 'not_found':
      return t('not_found', 'This file was removed or you are no longer in this conversation.');
    case 'network':
      return t('network', 'Couldn’t reach KaziPeople. Check your connection and try again.');
    default:
      return getApiErrorMessage(e, t('load_failed', 'The file couldn’t be opened right now. Try again.'));
  }
}

/** A blob error body (responseType 'blob') hides the server's JSON — unwrap it so
 *  getApiErrorMessage can read `{ message }`. Best-effort; never throws. */
async function unwrapBlobError(e: unknown): Promise<unknown> {
  const res = (e as HttpLikeError)?.response;
  const data = res?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      res!.data = JSON.parse(await readBlobText(data));
    } catch {
      /* not JSON — leave as is */
    }
  }
  return e;
}

function readBlobText(b: Blob): Promise<string> {
  if (typeof b.text === 'function') return b.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(r.error);
    r.readAsText(b);
  });
}

export interface FetchDeps {
  /** GET the file as a Blob (throws axios-style errors). */
  get: () => Promise<Blob>;
  /** Renew the session cookie; true when it worked. */
  refresh: () => Promise<boolean>;
  /** Pause before the single network retry (injectable for tests). */
  wait?: (ms: number) => Promise<void>;
}

/**
 * Fetch an attachment's bytes. 401 → renew the session once and retry once;
 * network failure → retry once. Never loops. Throws the (unwrapped) error.
 */
export async function fetchAttachmentBlob(deps: FetchDeps): Promise<Blob> {
  const wait = deps.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let renewed = false;
  let retriedNetwork = false;
  // At most three attempts: original, +1 after renewal, +1 after a network blip.
  for (;;) {
    try {
      return await deps.get();
    } catch (e) {
      const status = (e as HttpLikeError)?.response?.status;
      if (status === 401 && !renewed) {
        renewed = true;
        if (await deps.refresh()) continue;
      } else if (!status && !retriedNetwork) {
        retriedNetwork = true;
        await wait(800);
        continue;
      }
      throw await unwrapBlobError(e);
    }
  }
}

/** Save a blob under the ORIGINAL filename (never the URL's last segment). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
